import React, { useState, useEffect } from 'react';
import './client-css/client-dashboard-global.css';
import './client-css/client-dashboard-dark.css';
import './client-css/client-dashboard-light.css';
import MarqueeChip from '../../components/MarqueeChip';
import {
  Box,
  Typography,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import axios from 'axios';
import api from '../../utils/api';
import { getApiUrl } from '../../utils/apiUrl';
import { useAuth } from '../../contexts/AuthContext';

// Import the actual viewer dashboard components
import DashboardViewer from './DashboardViewer';
import AssignedTasks from './AssignedTasks';
import UserStatus from './UserStatus';

interface Company {
  _id: string;
  companyName: string;
  companyEmail: string;
  companyIndustry: string;
  maxUsers: number;
  urlSlug: string;
  branding?: any;
}

const SuperAdminViewer: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyData, setCompanyData] = useState<Company | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    // Wait for auth context to initialize before fetching data
    if (isAuthenticated !== undefined) {
      fetchCompanyData();
    }
  }, [isAuthenticated]);

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check for temporary access token - first from URL, then from sessionStorage
      const urlToken = new URLSearchParams(window.location.search).get('token');
      const tempToken = urlToken || sessionStorage.getItem('temp_client_token');
      
      if (!tempToken) {
        throw new Error('No temporary access token found');
      }

      // Store the token in sessionStorage for this window if it came from URL
      if (urlToken && !sessionStorage.getItem('temp_client_token')) {
        sessionStorage.setItem('temp_client_token', urlToken);
        // SuperAdminViewer: Stored temporary token from URL
      }

      // Get company slug from URL
      const pathParts = window.location.pathname.split('/');
      const companySlug = pathParts[1];

      // SuperAdminViewer: Fetching data for company

      // Fetch company data by slug - let api interceptor handle the token
      const companyResponse = await api.get(`/api/companies/slug/${companySlug}`);

      if (companyResponse.data.success) {
        setCompanyData(companyResponse.data.company);
        // SuperAdminViewer: Company data loaded successfully
      } else {
        throw new Error('Failed to load company data');
      }

    } catch (error: any) {
      console.error('Error fetching company data:', error);
      
      // Get company slug for error message
      const pathParts = window.location.pathname.split('/');
      const companySlug = pathParts[1];
      
      if (error.response?.status === 404) {
        setError(`Company with slug '${companySlug}' not found. Please check the URL.`);
      } else if (error.response?.status === 500) {
        setError('Server error occurred while fetching company data. Please try again.');
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to fetch company data. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading || isAuthenticated === undefined) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!companyData) {
    return (
      <Box p={3}>
        <Alert severity="warning">No company data available</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%' }}>
      {/* Show Actual Client Dashboard Components - Navigation handled by layout */}
      <DashboardViewer />
    </Box>
  );
};

export default SuperAdminViewer;